/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import AvatarEditor from 'react-avatar-editor';
import Dropzone from 'react-dropzone';
import ReactLoading from 'react-loading';
import { IoIosClose, IoMdCloseCircle } from 'react-icons/io';
import { BiTrash } from 'react-icons/bi';
import { toast } from 'react-toastify';

import Button from 'components/ui/button';
import { useModal } from 'context/ModalContext';
import { useAuth } from 'context/AuthContext';
import { getStylesAi, CarnivalStyle, CarnivalImageData } from 'services/carnival.service';
import { registerInteractionCampaign } from 'services/actionlog.service';
import { getGlobalConfig } from 'services/global.service';
import {
  generateImageViaTrigger,
  parseDataUri,
  ensureDataUri,
  deriveProvider,
  saveGeneratedImageViaTrigger,
  TriggerImageResult,
} from 'services/image-generation.service';

import logoCarnaval from 'assets/img/logo-carnaval.svg';
import molduraBb from 'assets/img/carnaval/moldura-bb.png';

const MODAL_ID = 'carnival-ai';

interface CarnivalAiModalProps {
  carnivalData?: CarnivalImageData | null;
  onImageGenerated?: () => void;
}

function pickRandomPromptStyle(promptStyle: string | string[]) {
  if (typeof promptStyle === 'string') {
    return promptStyle.trim();
  }

  const validPrompts = promptStyle.filter((prompt): prompt is string => typeof prompt === 'string' && prompt.trim().length > 0);
  if (!validPrompts.length) {
    return '';
  }

  const randomIndex = Math.floor(Math.random() * validPrompts.length);
  return validPrompts[randomIndex];
}

export function CarnivalAiModal({ carnivalData, onImageGenerated }: CarnivalAiModalProps) {
  const { openModal, closeModal } = useModal();
  const { user } = useAuth();

  const isOpen = openModal === MODAL_ID;
  const limitReached = carnivalData?.limitReached ?? false;
  const maxAttempts = (carnivalData?.limit ?? 0) > 0 ? (carnivalData?.limit ?? 0) : 3;
  const usedAttempts = carnivalData?.total ?? 0;
  const remainingAttempts = Math.max(maxAttempts - usedAttempts, 0);
  const savedImageUrl = carnivalData?.latest?.url || carnivalData?.latest?.imageUrl || '';
  const latestGeneratedUrl = carnivalData?.latestGenerated?.url || carnivalData?.latestGenerated?.imageUrl || '';
  const existingImageUrl = savedImageUrl || latestGeneratedUrl;
  const hasSavedImage = !!savedImageUrl;
  const userOriginalImageUrl = (typeof user?.image?.original?.url === 'string' ? user.image.original.url : '').trim();

  const [step, setStep] = useState(0);
  const [styles, setStyles] = useState<CarnivalStyle[]>([]);
  const [selectedStyle, setSelectedStyle] = useState<CarnivalStyle | null>(null);
  const [image, setImage] = useState<File | string>('');
  const [skipAutoLoadUserImage, setSkipAutoLoadUserImage] = useState(false);
  const [photoDataUrl, setPhotoDataUrl] = useState<string>('');
  const [generatedImage, setGeneratedImage] = useState<string>('');
  const [generatedResult, setGeneratedResult] = useState<TriggerImageResult | null>(null);
  const [savedGeneratedImage, setSavedGeneratedImage] = useState(false);
  const [error, setError] = useState<string>('');
  const [sessionStartUsedAttempts, setSessionStartUsedAttempts] = useState(usedAttempts);
  const [sessionGeneratedCount, setSessionGeneratedCount] = useState(0);

  const [loadingStyles, setLoadingStyles] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savingGeneratedImage, setSavingGeneratedImage] = useState(false);
  const [photoAIEnabled, setPhotoAIEnabled] = useState<boolean | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  const effectiveUsedAttempts = Math.max(usedAttempts, sessionStartUsedAttempts + sessionGeneratedCount);
  const remainingAttemptsAfterGeneration = Math.max(maxAttempts - effectiveUsedAttempts, 0);

  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const editorRef = useRef<any>(null);
  const preloadedUserImageBlobUrlRef = useRef<string | null>(null);

  const getFocusableElements = useCallback(() => {
    const focusableElements = modalRef.current?.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    return focusableElements ? (Array.from(focusableElements) as HTMLElement[]) : [];
  }, []);

  const handleModalKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (e.key !== 'Tab') return;

      e.preventDefault();

      const focusableElements = getFocusableElements();
      if (!focusableElements.length) return;

      const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
      let nextIndex = 0;

      if (e.shiftKey) {
        nextIndex = currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= focusableElements.length - 1 ? 0 : currentIndex + 1;
      }

      focusableElements[nextIndex].focus();
    },
    [getFocusableElements],
  );

  function clearPreloadedUserImageBlobUrl() {
    if (preloadedUserImageBlobUrlRef.current) {
      URL.revokeObjectURL(preloadedUserImageBlobUrlRef.current);
      preloadedUserImageBlobUrlRef.current = null;
    }
  }

  function toRegionalS3Url(url: string) {
    return url.replace(
      /^https:\/\/s3\.amazonaws\.com\/([^/]+)\/(.+)$/,
      'https://$1.s3.amazonaws.com/$2',
    );
  }

  function trackInteraction(interaction: string, extra?: Record<string, unknown>) {
    registerInteractionCampaign('me', 'carnaval_2026', {
      interaction,
      ...extra,
    });
  }

  const stepRefs = {
    0: useRef<HTMLDivElement>(null),
    1: useRef<HTMLDivElement>(null),
    2: useRef<HTMLDivElement>(null),
    3: useRef<HTMLDivElement>(null),
    4: useRef<HTMLDivElement>(null),
    5: useRef<HTMLDivElement>(null),
  };

  // Focus management on step change
  useEffect(() => {
    if (!isOpen) return;
    const ref = stepRefs[step as keyof typeof stepRefs];
    if (ref?.current) {
      setTimeout(() => {
        ref.current?.querySelector('h2')?.focus();
      }, 300);
    }
  }, [step, isOpen]);

  // Check global config when modal opens
  useEffect(() => {
    if (isOpen) {
      checkPhotoAIEnabled();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSessionStartUsedAttempts(usedAttempts);
    setSessionGeneratedCount(0);
  }, [isOpen]);

  async function checkPhotoAIEnabled() {
    setLoadingConfig(true);
    try {
      const data = await getGlobalConfig('global');
      const config = Array.isArray(data) ? data[0] : data;
      const enabled = config?.photoAIEnabled !== false;
      setPhotoAIEnabled(enabled);
      if (!enabled) {
        trackInteraction('disabled');
      }
      if (enabled && styles.length === 0) {
        loadStyles();
      }
    } catch (e) {
      console.error('Erro ao verificar configuracao:', e);
      setPhotoAIEnabled(true);
      if (styles.length === 0) {
        loadStyles();
      }
    } finally {
      setLoadingConfig(false);
    }
  }

  async function loadStyles() {
    setLoadingStyles(true);
    try {
      const result = await getStylesAi();
      if (result && result.length > 0) {
        setStyles(result);
      }
    } catch (e) {
      console.error('Erro ao carregar estilos:', e);
    } finally {
      setLoadingStyles(false);
    }
  }

  async function extractPhotoAndAdvance() {
    if (!editorRef.current) return;

    // Re-check global config before generating
    try {
      const data = await getGlobalConfig('global');
      const config = Array.isArray(data) ? data[0] : data;
      if (config?.photoAIEnabled === false) {
        setPhotoAIEnabled(false);
        setStep(0);
        return;
      }
    } catch (e) {
      // On error, allow to proceed
    }

    const dataUrl = editorRef.current.getImageScaledToCanvas().toDataURL();
    setPhotoDataUrl(dataUrl);
    setStep(3);
  }

  // Trigger generation when entering step 3
  useEffect(() => {
    if (step === 3 && photoDataUrl && selectedStyle && !generating) {
      handleGenerate();
    }
  }, [step, photoDataUrl]);

  // Preload user's current profile image on upload step
  useEffect(() => {
    let isCancelled = false;

    async function preloadUserImage() {
      if (!isOpen || step !== 2 || image || !userOriginalImageUrl || skipAutoLoadUserImage) return;

      const regionalUrl = toRegionalS3Url(userOriginalImageUrl);
      // Always use regional URL for preview on upload step
      setImage(regionalUrl);

      try {
        const response = await fetch(regionalUrl, { mode: 'cors' });

        if (!response.ok) {
          throw new Error(`Erro ao buscar imagem convertida: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        if (isCancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }

        clearPreloadedUserImageBlobUrl();
        preloadedUserImageBlobUrlRef.current = blobUrl;
        setImage(blobUrl);
      } catch (error) {
        // Keep regional URL as fallback
      }
    }

    preloadUserImage();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, step, image, userOriginalImageUrl, skipAutoLoadUserImage]);

  useEffect(() => {
    return () => {
      clearPreloadedUserImageBlobUrl();
    };
  }, []);

  async function handleGenerate() {
    if (!selectedStyle || !photoDataUrl || !user) return;

    setGenerating(true);
    setError('');
    trackInteraction('generate_start', { style: selectedStyle.name, provider: deriveProvider(selectedStyle.recommended.model) });

    try {
      const { mimeType, base64Data } = parseDataUri(photoDataUrl);
      const provider = deriveProvider(selectedStyle.recommended.model);
      const promptStyle = pickRandomPromptStyle(selectedStyle.promptStyle);

      if (!promptStyle) {
        throw new Error('Prompt do estilo invalido');
      }

      const result = await generateImageViaTrigger(promptStyle, provider, base64Data, mimeType, user._id, selectedStyle.name);

      if (!result.imageUrl && !result.imageBase64) {
        throw new Error('Imagem gerada esta vazia');
      }

      const imageResult = result.imageUrl || ensureDataUri(result.imageBase64!, mimeType);
      setGeneratedImage(imageResult);
      setGeneratedResult(result);
      setSavedGeneratedImage(!!result.saved);
      setSessionGeneratedCount(previousCount => previousCount + 1);
      setStep(4);
      trackInteraction('generate_success', { style: selectedStyle.name });
      onImageGenerated?.();
    } catch (err: any) {
      console.error('Erro na geracao:', err);
      const errorMsg = err?.message || 'Erro ao gerar imagem.';
      setError(errorMsg);
      trackInteraction('generate_error', { style: selectedStyle.name, error: errorMsg });
      setStep(2);
      toast('Erro ao gerar imagem. Tente novamente.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setGenerating(false);
    }
  }

  function handleClose() {
    clearPreloadedUserImageBlobUrl();
    setStep(0);
    setImage('');
    setSkipAutoLoadUserImage(false);
    setPhotoDataUrl('');
    setSelectedStyle(null);
    setGeneratedImage('');
    setGeneratedResult(null);
    setSavedGeneratedImage(false);
    setError('');
    setGenerating(false);
    setSavingGeneratedImage(false);
    setSessionGeneratedCount(0);
    closeModal();
  }

  async function handleSaveGeneratedImage() {
    if (!generatedResult || savingGeneratedImage || savedGeneratedImage) return;

    setSavingGeneratedImage(true);
    trackInteraction('save_start', { style: selectedStyle?.name });

    try {
      const savedResult = await saveGeneratedImageViaTrigger(generatedResult);
      setGeneratedResult(savedResult);
      setSavedGeneratedImage(true);
      trackInteraction('save_success', { style: selectedStyle?.name });
      toast('Imagem salva com sucesso!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      onImageGenerated?.();
    } catch (e: any) {
      const errorMsg = e?.message || 'Erro ao salvar imagem.';
      trackInteraction('save_error', { style: selectedStyle?.name, error: errorMsg });
      toast('Erro ao salvar imagem. Tente novamente.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setSavingGeneratedImage(false);
    }
  }

  function handleGenerateAgain() {
    setGeneratedImage('');
    setGeneratedResult(null);
    setSavedGeneratedImage(false);
    setError('');
    setGenerating(false);
    setSavingGeneratedImage(false);
    setStep(1);
  }

  // Track modal open and handle saved/limit states
  useEffect(() => {
    if (!isOpen || step !== 0) return;

    if (limitReached) {
      trackInteraction('open_limit_reached', { total: carnivalData?.total, limit: carnivalData?.limit, hasSavedImage });
      setStep(5);
      return;
    }

    if (hasSavedImage) {
      trackInteraction('open_saved_image', { total: carnivalData?.total, limit: carnivalData?.limit });
      setStep(5);
      return;
    }

    trackInteraction('open', { total: carnivalData?.total, limit: carnivalData?.limit });
  }, [isOpen, step, limitReached, hasSavedImage]);

  async function handleDownloadImage(imageUrl: string) {
    trackInteraction('download');
    try {
      // Convert global S3 URL to regional format for CORS
      const regionalUrl = imageUrl.replace(
        /^https:\/\/s3\.amazonaws\.com\/([^/]+)\/(.+)$/,
        'https://$1.s3.amazonaws.com/$2',
      );
      const response = await fetch(regionalUrl, { mode: 'cors' });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'carnaval-jogajunto.png';
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      // Fallback: open in new tab
      window.open(imageUrl, '_blank');
    }
  }

  function downloadFromDataUrl(dataUrl: string, filename: string) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    link.click();
  }

  function loadImageElement(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  async function createFramedImageDataUrl(baseImageUrl: string) {
    const baseImage = await loadImageElement(baseImageUrl);
    const frameImage = await loadImageElement(molduraBb);

    const width = baseImage.naturalWidth || baseImage.width;
    const height = baseImage.naturalHeight || baseImage.height;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Nao foi possivel criar contexto do canvas');
    }

    context.drawImage(baseImage, 0, 0, width, height);
    context.drawImage(frameImage, 0, 0, width, height);

    return canvas.toDataURL('image/png');
  }

  async function handleDownloadGeneratedImageWithFrame() {
    trackInteraction('download');
    try {
      const framedDataUrl = await createFramedImageDataUrl(generatedImage);
      downloadFromDataUrl(framedDataUrl, 'carnaval-jogajunto.png');
    } catch (e) {
      await handleDownloadImage(generatedImage);
    }
  }

  async function handleDownloadExistingImageWithFrame() {
    if (!existingImageUrl) return;
    trackInteraction('download');
    try {
      const framedDataUrl = await createFramedImageDataUrl(existingImageUrl);
      downloadFromDataUrl(framedDataUrl, 'carnaval-jogajunto.png');
    } catch (e) {
      await handleDownloadImage(existingImageUrl);
    }
  }

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Save and restore focus around modal lifecycle
  useEffect(() => {
    if (!isOpen) return;

    previousActiveElementRef.current = document.activeElement as HTMLElement;

    setTimeout(() => {
      const focusableElements = getFocusableElements();
      if (focusableElements.length) {
        focusableElements[0].focus();
      } else if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      }
    }, 100);

    return () => {
      if (previousActiveElementRef.current) {
        previousActiveElementRef.current.focus();
      }
    };
  }, [isOpen, getFocusableElements]);

  // Lock page scroll while modal is open; keep scroll inside overlay
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const totalSteps = 3;
  const progressCurrentStep = step === 1 ? 1 : step === 2 || step === 3 ? 2 : step === 4 ? 3 : 0;

  return (
    <div
      className="fixed z-20 top-0 left-0 w-full h-full bg-black bg-opacity-80 flex flex-col items-center justify-start overflow-y-auto overscroll-contain p-4 pt-20 pb-6"
      role="dialog"
      aria-modal="true"
      aria-label="Gerar foto de Carnaval com IA"
    >
      <div
        ref={modalRef}
        onKeyDown={handleModalKeyDown}
        className={`relative border-2 border-electric-blue bg-oxford-blue rounded-2xl lg:rounded-[20px] p-4 lg:p-8 flex flex-col w-[95%] lg:w-auto items-center my-3 ${
          step === 1 ? 'lg:max-w-[900px]' : 'lg:max-w-[600px]'
        }`}
      >
        {/* Close button */}
        <button
          ref={closeButtonRef}
          type="button"
          className="absolute -top-5 -right-5 z-10 bg-purple text-white Button-close b rounded-r-full rounded-l-full w-10 h-10 flex items-center justify-center p-0 hover:ring hover:ring-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
          onClick={handleClose}
          aria-label="Fechar modal de foto de Carnaval"
        >
          <IoIosClose color="white" size={30} />
        </button>

        {/* Step indicator - hidden on intro and loading */}
        {step > 0 && step !== 3 && step !== 5 && (
          <div className="relative flex flex-row items-center justify-around w-full mx-8 mb-6" aria-hidden={true}>
            <div className="absolute z-0 w-full h-[1px] top-0 bottom-0 m-auto border-b border-white opacity-20" />
            {Array.from({ length: totalSteps }, (_, i) => (
              // display 3 logical steps: 1=estilo, 2=imagem, 3=resultado
              <div
                key={i}
                className={`${
                  progressCurrentStep === i + 1 ? 'bg-slate-blue' : i + 1 < progressCurrentStep ? 'bg-purple' : 'bg-prussian-blue'
                } text-white text-xs relative ring-1 ring-white rounded-full w-5 h-5 flex flex-row items-center justify-center`}
              >
                {i + 1}
              </div>
            ))}
          </div>
        )}

        {/* Step 0: Welcome */}
        {step === 0 && (
          <div className="text-center space-y-4 p-4" ref={stepRefs[0]}>
            <img src={logoCarnaval} alt="Carnaval 2026" className="w-40 mx-auto" />

            {loadingConfig ? (
              <div className="flex flex-col items-center py-8">
                <ReactLoading type="bubbles" color="#fbfd00" height={60} width={60} />
              </div>
            ) : photoAIEnabled === false ? (
              <>
                <h2 className="text-yellow text-2xl font-bold" tabIndex={0}>
                  Carnaval 2026
                </h2>
                <p className="text-white text-base font-light max-w-md mx-auto">
                   A geração de fotos de Carnaval está temporariamente desativada. Tente novamente mais tarde!
                </p>
                <p className="text-yellow font-bold">💛 #JogaJunto</p>
                <Button color="outline" className="uppercase py-2 px-6 rounded" onClick={handleClose}>
                  FECHAR
                </Button>
              </>
            ) : (
              <>
                <h2 className="text-yellow text-2xl font-bold" tabIndex={0}>
                  Novo Ciclo do Joga Junto no ar!
                </h2>
                <div className="text-white text-sm font-light max-w-md mx-auto text-left space-y-3">
                  <p>
                    Entre no clima da folia e personalize um card especial de Carnaval, no estilo das trends que estão movimentando as redes sociais!
                  </p>
                  <div>
                    <p className="font-medium mb-1">É simples:</p>
                    <p>📷 Escolha sua foto — pode subir uma nova ou usar a que você já tem.</p>
                    <p>🎨 Customize do seu jeito — selecione o tema que combina mais com seu estilo de Carnaval.</p>
                    {/*<p>
                      {remainingAttempts}x Você pode gerar até {remainingAttempts} {remainingAttempts === 1 ? 'imagem' : 'imagens'}. Se gostou, salve seu card e compartilhe.
                    </p>
                    <p>💾 Depois que salvar, não será possível gerar nova imagem.</p>
                    <p>⛔ Quando as tentativas acabarem, não será mais possível gerar sua imagem de Carnaval.</p>*/}
                  </div>
                  <p className="text-center font-medium">
                    Participe, personalize e venha celebrar este novo ciclo com a gente!
                  </p>
                  <p className="text-yellow text-center font-bold">
                    💛 #JogaJunto
                  </p>
                </div>
                <Button
                  color="featured"
                  className="uppercase py-3 px-8 rounded text-lg"
                  onClick={() => { trackInteraction('start'); setStep(1); }}
                  arialabel="Comecar a criar foto de Carnaval"
                >
                  COMEÇAR
                </Button>
              </>
            )}
          </div>
        )}

        {/* Step 1: Style Selection */}
        {step === 1 && (
          <div className="space-y-4 p-2 lg:p-4 w-full" ref={stepRefs[1]}>
            <span className="screen-reader-text" tabIndex={0}>
              Etapa 1 de 3: Escolha um estilo de Carnaval.
            </span>
            <h2 className="text-yellow text-xl font-bold text-center" tabIndex={0}>
              Escolha seu estilo
            </h2>

            {loadingStyles ? (
              <div className="flex flex-col items-center justify-center py-8">
                <ReactLoading type="bubbles" color="#fbfd00" height={60} width={60} />
                <p className="text-white animate-pulse text-center mt-2">Carregando estilos...</p>
              </div>
            ) : styles.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-white mb-4">Nenhum estilo disponivel no momento.</p>
                <Button color="outline" onClick={loadStyles}>
                  TENTAR NOVAMENTE
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-5 w-full">
                {styles.map(style => (
                  <button
                    key={style._id}
                    onClick={() => setSelectedStyle(style)}
                    className={`rounded-xl overflow-hidden border-2 transition-all ${
                      selectedStyle?._id === style._id
                        ? 'border-yellow ring-2 ring-yellow'
                        : 'border-electric-blue border-opacity-30 hover:border-opacity-100'
                    } bg-maastricht-blue`}
                    aria-label={`Estilo: ${style.name}`}
                    aria-pressed={selectedStyle?._id === style._id}
                  >
                    <img src={style.image.original.url} alt={style.name} className="w-full h-44 lg:h-56 object-cover" />
                    <p className="text-white text-xs lg:text-sm p-2 text-center font-medium">{style.name}</p>
                  </button>
                ))}
              </div>
            )}

            {selectedStyle && (
              <p className="text-electric-blue text-sm text-center font-light mt-2">{selectedStyle.description}</p>
            )}
          </div>
        )}

        {/* Step 2: Photo Upload */}
        {step === 2 && (
          <div className="space-y-4 p-2 lg:p-4 flex flex-col items-center w-full" ref={stepRefs[2]}>
            <span className="screen-reader-text" tabIndex={0}>
              Etapa 2 de 3: Envie sua foto.
            </span>
            <h2 className="text-yellow text-xl font-bold text-center" tabIndex={0}>
              Envie sua foto
            </h2>
            <p className="text-white text-sm text-center font-light">
              Arraste uma foto ou clique para selecionar. Ajuste o enquadramento como desejar.
            </p>

            {error && (
              <div className="bg-red-600 bg-opacity-20 border border-red-500 rounded-lg p-3 w-full max-w-[280px]">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            <Dropzone
              onDrop={(dropped: File[]) => {
                clearPreloadedUserImageBlobUrl();
                setImage(dropped[0]);
              }}
              multiple={false}
              maxFiles={1}
              noClick={!!image}
              accept={{
                'image/png': ['.png', '.jpeg', '.jpg'],
              }}
            >
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()} className="relative">
                  {!image ? (
                    <div className="w-64 h-64 border-2 border-dashed border-electric-blue border-opacity-50 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-opacity-100 hover:bg-maastricht-blue transition-all">
                      <BiTrash size={0} className="hidden" />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-16 h-16 text-electric-blue opacity-50 mb-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-electric-blue text-sm text-center px-4">Clique ou arraste sua foto aqui</p>
                    </div>
                  ) : (
                    <div className="relative rounded-lg overflow-hidden">
                      <AvatarEditor
                        ref={(e: any) => (editorRef.current = e)}
                        width={256}
                        height={256}
                        image={image}
                        crossOrigin="anonymous"
                        borderRadius={0}
                        border={0}
                        className="overflow-hidden rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          clearPreloadedUserImageBlobUrl();
                          setSkipAutoLoadUserImage(true);
                          setImage('');
                          setError('');
                        }}
                        className="absolute bottom-2 right-2 rounded-full bg-white text-red-500 w-10 h-10 flex items-center justify-center"
                        aria-label="Remover foto selecionada"
                      >
                        <BiTrash size={24} />
                      </button>
                    </div>
                  )}
                  <input {...getInputProps()} aria-label="Selecionar foto para upload" />
                </div>
              )}
            </Dropzone>
          </div>
        )}

        {/* Step 3: Generating */}
        {step === 3 && (
          <div className="flex flex-col items-center justify-center space-y-6 p-8 min-h-[300px]" ref={stepRefs[3]}>
            <span className="screen-reader-text" tabIndex={0} aria-live="polite">
              Etapa 2 de 3: Gerando sua imagem de Carnaval. Por favor aguarde.
            </span>
            <ReactLoading type="bubbles" color="#fbfd00" height={80} width={80} />
            <p className="text-yellow text-xl font-bold animate-pulse">Gerando sua foto de Carnaval...</p>
            <p className="text-white text-sm font-light text-center">Isso pode levar alguns segundos. NÃO feche esta janela.</p>
          </div>
        )}

        {/* Step 4: Result */}
        {step === 4 && generatedImage && (
          <div className="flex flex-col items-center space-y-5 p-2 lg:p-4" ref={stepRefs[4]}>
            <span className="screen-reader-text" tabIndex={0}>
              Etapa 3 de 3: Sua foto de Carnaval foi gerada com sucesso!
            </span>
            <h2 className="text-yellow text-xl font-bold" tabIndex={0}>
              Sua foto ficou incrivel!
            </h2>

            <p className="text-white text-xs text-center font-light max-w-[400px]">
              Esta imagem foi gerada com recursos de inteligência artificial e pode cometer erros.
            </p>

            <div className="relative rounded-xl overflow-hidden border-2 border-electric-blue shadow-2xl w-full max-w-[400px]">
              <img src={generatedImage} alt="Foto de Carnaval gerada por IA" className="w-full h-auto block" />

              {/* MOLDURA DESCOMENTAR PARA ATIVAR */}

              {/* <img
                src={molduraBb}
                alt=""
                aria-hidden={true}
                className="absolute inset-0 w-full h-full object-contain pointer-events-none"
              /> */}
            </div>

            {!savedGeneratedImage && (
              <p
                className={`text-sm text-center font-medium max-w-[400px] ${
                  remainingAttemptsAfterGeneration > 0 ? 'text-white' : 'text-yellow'
                }`}
              >
                {remainingAttemptsAfterGeneration > 0
                  ? '' 
                  // `Você ainda tem ${remainingAttemptsAfterGeneration} ${
                  //     remainingAttemptsAfterGeneration === 1 ? 'tentativa' : 'tentativas'
                  //   }. Depois que salvar, não será possível gerar nova imagem.`
                  : 'Suas tentativas acabaram. Não é mais possível gerar nova imagem de Carnaval.'}
              </p>
            )}

            <div className="flex flex-row items-center justify-center space-x-4">
              {remainingAttemptsAfterGeneration > 0 && (
                <button
                  onClick={handleGenerateAgain}
                  className="w-auto bg-electric-blue text-center text-oxford-blue hover:text-oxford-blue active:text-oxford-blue focus:text-oxford-blue hover:ring hover:ring-white rounded-md shadow-xl font-bold px-6 py-3 uppercase inline-block no-underline hover:no-underline active:no-underline focus:no-underline"
                >
                  Gerar novamente
                </button>
              )}
              {!savedGeneratedImage && (
                <button
                  onClick={handleSaveGeneratedImage}
                  disabled={savingGeneratedImage || !generatedResult}
                  className="w-auto bg-slate-blue text-center text-white disabled:opacity-50 hover:text-white active:text-white focus:text-white hover:ring hover:ring-white rounded-md shadow-xl font-bold px-6 py-3 uppercase inline-block no-underline hover:no-underline active:no-underline focus:no-underline"
                >
                  {savingGeneratedImage ? 'Salvando...' : 'Salvar imagem'}
                </button>
              )}
              {savedGeneratedImage && (
                <button
                  onClick={handleDownloadGeneratedImageWithFrame}
                  className="w-auto bg-purple text-center text-white hover:text-white active:text-white focus:text-white hover:ring hover:ring-white rounded-md shadow-xl font-bold px-6 py-3 uppercase inline-block no-underline hover:no-underline active:no-underline focus:no-underline"
                >
                  Baixar imagem
                </button>
              )}
              <Button color="primary" className="uppercase py-2 px-5 rounded" onClick={handleClose}>
                FECHAR
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: View only (saved/limit states) */}
        {step === 5 && (
          <div className="flex flex-col items-center space-y-5 p-2 lg:p-4" ref={stepRefs[5]}>
            <h2 className="text-yellow text-xl font-bold" tabIndex={0}>
              Sua foto de Carnaval
            </h2>
            {limitReached ? (
              hasSavedImage ? (
                <p className="text-white text-sm text-center font-light">
                  Você já atingiu o limite de {carnivalData?.limit} {(carnivalData?.limit ?? 0) === 1 ? 'foto' : 'fotos'} geradas.
                </p>
              ) : (
                <p className="text-white text-sm text-center font-light">
                  Você atingiu o limite de {carnivalData?.limit} {(carnivalData?.limit ?? 0) === 1 ? 'foto' : 'fotos'} geradas e não salvou nenhuma. Salve da próxima vez para aparecer no seu painel.
                </p>
              )
            ) : (
              <p className="text-white text-sm text-center font-light">
                Sua imagem já está salva. Você pode baixar quando quiser.
              </p>
            )}

            {existingImageUrl && (
              <div className="relative rounded-xl overflow-hidden border-2 border-electric-blue shadow-2xl w-full max-w-[400px]">
                <img src={existingImageUrl} alt="Minha foto de Carnaval" className="w-full h-auto block" />
                {/* MOLDURA DESCOMENTAR PARA ATIVAR */}

                {/* <img
                  src={molduraBb}
                  alt=""
                  aria-hidden={true}
                  className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                /> */}
              </div>
            )}

            <div className="flex flex-row items-center justify-center space-x-4">
              {/* {remainingAttempts > 0 && (
                <button
                  onClick={() => {
                    trackInteraction('generate_again_saved_view');
                    handleGenerateAgain();
                  }}
                  className="w-auto bg-electric-blue text-center text-oxford-blue hover:text-oxford-blue active:text-oxford-blue focus:text-oxford-blue hover:ring hover:ring-white rounded-md shadow-xl font-bold px-6 py-3 uppercase inline-block no-underline hover:no-underline active:no-underline focus:no-underline"
                >
                  Gerar novamente
                </button>
              )} */}
              {existingImageUrl && (
                <button
                  onClick={handleDownloadExistingImageWithFrame}
                  className="w-auto bg-purple text-center text-white hover:text-white active:text-white focus:text-white hover:ring hover:ring-white rounded-md shadow-xl font-bold px-6 py-3 uppercase inline-block no-underline hover:no-underline active:no-underline focus:no-underline"
                >
                  Baixar imagem
                </button>
              )}
              <Button color="primary" className="uppercase py-2 px-5 rounded" onClick={handleClose}>
                FECHAR
              </Button>
            </div>
          </div>
        )}

        {/* Navigation buttons (steps 1 and 2) */}
        {(step === 1 || step === 2) && (
          <div className="flex flex-row items-center justify-center space-x-4 w-full px-4 pt-4">
            <button
              onClick={() => setStep(step - 1)}
              aria-label={`Voltar para etapa ${step}`}
              className="leading-none ring-2 ring-blue-violet-crayola flex items-center bg-transparent text-center text-white rounded py-3 px-4"
            >
              <p className="font-bold text-sm leading-none uppercase">Voltar</p>
            </button>

            {step === 1 && (
              <button
                onClick={() => { trackInteraction('select_style', { style: selectedStyle?.name }); setStep(2); }}
                disabled={!selectedStyle}
                aria-label="Continuar para envio de foto"
                className="leading-none disabled:opacity-50 ring-2 ring-slate-blue flex items-center bg-slate-blue text-center text-white rounded py-3 px-4"
              >
                <p className="font-bold text-sm leading-none uppercase">Continuar</p>
              </button>
            )}

            {step === 2 && (
              <button
                onClick={extractPhotoAndAdvance}
                disabled={!image}
                aria-label="Gerar foto de Carnaval"
                className="leading-none disabled:opacity-50 ring-2 ring-slate-blue flex items-center bg-slate-blue text-center text-white rounded py-3 px-4"
              >
                <p className="font-bold text-sm leading-none uppercase">Gerar</p>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


